/**
 * Mysa Buy Buttons
 *
 * Handles two concerns not covered by the upstream ProductFormComponent:
 *
 * 1. Bundle fallback URL — updates the cart permalink quantity in
 *    utility-bundle fallback links when the quantity selector changes.
 *
 * 2. Checkout redirect — the "Buy now" button triggers the form's
 *    existing submit handler (ProductFormComponent.handleSubmit) and
 *    redirects to /checkout on success. Reuses all upstream error
 *    handling, live regions, and cart events.
 *
 * Quantity sync is handled natively via the HTML form attribute on the
 * quantity selector input — no JS bridge needed.
 */

import { ThemeEvents } from "@theme/events";
import { safeSubmit } from "@theme/mysa-utilities";

/**
 * Updates the cart permalink quantity in the bundle fallback block.
 * Cart permalink format: /cart/{variantId}:{quantity}?discount=...
 * @param {number} quantity
 */
function updateBundleFallbackQuantity(quantity) {
  const fallbackBlock = document.querySelector(
    ".buy-buttons-block--bundle-fallback",
  );
  if (!fallbackBlock) return;

  const cartUrl = fallbackBlock.dataset.cartUrl;
  if (cartUrl) {
    fallbackBlock.dataset.cartUrl = cartUrl.replace(/:\d+/, `:${quantity}`);
  }

  const fallbackLink = fallbackBlock.querySelector(
    '[id^="BuyButtons-BundleFallback-"]',
  );

  if (!fallbackLink?.hasAttribute("href")) return;

  const href = fallbackLink.getAttribute("href");
  fallbackLink.setAttribute("href", href.replace(/:\d+/, `:${quantity}`));
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  if (event.detail.cartLine) return;
  updateBundleFallbackQuantity(event.detail.quantity);
});

const CHECKOUT_LISTENER_TIMEOUT = 15000;

document.addEventListener("click", (event) => {
  const button = event.target.closest('[id^="BuyButtons-CheckoutButton-"]');
  if (!button) return;

  const form = button.closest("form");
  if (!form) return;

  const productForm = form.closest("product-form-component");
  if (!productForm) return;

  const controller = new AbortController();

  productForm.addEventListener(
    ThemeEvents.cartUpdate,
    (cartEvent) => {
      controller.abort();
      if (cartEvent.detail?.data?.didError) return;
      window.location.href = "/checkout";
    },
    { signal: controller.signal },
  );

  setTimeout(() => controller.abort(), CHECKOUT_LISTENER_TIMEOUT);
  safeSubmit(form);
});
